import { ScrollRendererGotoScrollBehavior, type IFocusOnReadingPositionOptions, type IReaderViewGotoOptions } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import { CirrusReaderNavigationType, CirrusScrollRendererGotoScrollBehavior } from "../shared/CirrusReaderModel";
import type { ICirrusReaderNavigationAction, ICirrusReaderNavigationOptions } from "../shared/CirrusReaderModel";

let _hasOngoingFocusOnReadingPositionTask: boolean = false;
let _pendingFocusOnReadingPositionOptions: IFocusOnReadingPositionOptions | undefined;

export const useCirrusSharedAPI = () => {
    
    const { 
        $cirrusReader, 
        $cirrusReaderView
    } = useNuxtApp();

    const navigate = (
        navigationAction: ICirrusReaderNavigationAction,
        options?: ICirrusReaderNavigationOptions
    ): void => {

        switch (navigationAction.navigationType) {
            case CirrusReaderNavigationType.NEXT:
                $cirrusReaderView.next().catch(console.warn);
                break;
            case CirrusReaderNavigationType.PREVIOUS:
                    $cirrusReaderView.previous().catch(console.warn);
                break;
            case CirrusReaderNavigationType.TIMELINEPOSITION:
                if (navigationAction.position !== undefined) {
                    $cirrusReader.gotoTimelinePosition(navigationAction.position).catch(console.error);
                } else {
                    console.warn('CirrusSharedApi.navigate()', 'event.detail.position is undefined');
                }
                break;
            case CirrusReaderNavigationType.GOTO:
                if (navigationAction.locator) {
                    const goToOptions: IReaderViewGotoOptions | undefined =  options ? {
                        scrollRendererScrollBehavior: (
                            options.scrollRendererScrollBehavior &&
                            useCirrusReaderUtils().isColibrioScrollRendererGotoScrollBehavior(options.scrollRendererScrollBehavior)
                        ) ? options.scrollRendererScrollBehavior : ScrollRendererGotoScrollBehavior.SCROLL_IF_OUTSIDE_READING_AREA,
                        setReadingPositionToVisibleRangeStart: options.setReadingPositionToVisibleRangeStart ?? false

                    } : undefined;
                    $cirrusReaderView.goTo(navigationAction.locator, goToOptions).catch(console.error);
                } else {
                    console.warn('CirrusSharedApi.navigate()', 'event.detail.location is undefined');
                }
                break;
            case CirrusReaderNavigationType.GOTOSTART:
                $cirrusReaderView.goToStart().catch(console.error);
                break;
        }

        if ($cirrusReader.shouldFocusOnReadingPosition && Object.prototype.hasOwnProperty.call(navigationAction, 'focusNearContentLocation') && navigationAction.focusNearContentLocation) {
            const prev = useCirrusReaderNavigation().currentLocator;
        
            useCirrusReaderNavigation().$subscribe((mutation, state) => {
                if (state.currentLocator !== prev){
                    focusOnReadingPosition();
                }
            }, {
                once: true
            });
        }
    }

    const speakSelectedText = (text: string) => {
        $cirrusReader.speakText(text);
    }

    const focusOnReadingPosition = async (options?: IFocusOnReadingPositionOptions): Promise<void> => {
        if (_hasOngoingFocusOnReadingPositionTask) {
            _pendingFocusOnReadingPositionOptions = options; // keep latest
            return;
        }
        _hasOngoingFocusOnReadingPositionTask = true;
        try {
            let nextOptions = options;
            while (true) {
                await $cirrusReader.focusOnReadingPosition(nextOptions);
                if (!_pendingFocusOnReadingPositionOptions) break;
                nextOptions = _pendingFocusOnReadingPositionOptions;
                _pendingFocusOnReadingPositionOptions = undefined;
            }
        } finally {
            _hasOngoingFocusOnReadingPositionTask = false;
        }
    }


    const navigateTo = (locator: string, focusNearContentLocation: boolean = true): void => {
        navigate({
            navigationType: CirrusReaderNavigationType.GOTO,
            locator,
            focusNearContentLocation: focusNearContentLocation,
        }, {
            scrollRendererScrollBehavior: CirrusScrollRendererGotoScrollBehavior.SCROLL_IF_OUTSIDE_READING_AREA
        });
    }

    
    return {
        navigate,
        navigateTo,
        speakSelectedText,
        focusOnReadingPosition,
        get hasOngoingFocusOnReadingPositionTask(): boolean {
            return _hasOngoingFocusOnReadingPositionTask;
        },
        get appHasFocusWithin(): boolean {
            const root = document.getElementById("reader-view-element");
            const active = document.activeElement as HTMLElement | null;
            if (!root || !active) return false;

            if (active === root || root.contains(active)) return true;
            if (active.tagName === "IFRAME") return true; // nested reader iframe case

            return false;
        }
    };
};
